import { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../store/auth';
import { useProjectsStore } from '../store/projects';
import { usePostsStore, Post } from '../store/posts';
import { useFlightsStore, Flight } from '../store/flights';
import { api, getMediaUrl } from '../services/api';
import { POST_STATUS, GENERIC_STATS } from '../config/statusConfig';
import { StatsCard, StatsCardGrid } from '../components/StatsCard';
import { PageHeader } from '../components/PageHeader';

// HomeIcon - matches header navigation
function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  );
}

interface HealthStatus {
  status: string;
  version: string;
  timestamp: string;
  environment: string;
  services: {
    database: string;
  };
}

interface DashboardStats {
  totalPosts: number;
  scheduledPosts: number;
  publishedPosts: number;
  failedPosts: number;
  draftPosts: number;
  activeFlights: number;
  upcomingPosts: Post[];
  recentPublished: Post[];
  recentlyEdited: Array<{
    type: 'post' | 'flight' | 'project';
    id: string;
    displayId: string;
    title: string;
    updatedAt: string;
    thumbnail?: string;
    color?: string;
  }>;
}

export function DashboardPage() {
  const { t } = useTranslation(['dashboard', 'posts', 'common']);
  const { user, isAuthenticated } = useAuthStore();
  const { projects, fetchProjects, selectedProjectId, setSelectedProjectId } = useProjectsStore();
  const { posts, fetchPosts } = usePostsStore();
  const { flights, fetchFlights } = useFlightsStore();
  const navigate = useNavigate();
  const [health, setHealth] = useState<HealthStatus | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    api.get<HealthStatus>('/health')
      .then(setHealth)
      .catch(console.error);

    fetchProjects();
  }, [isAuthenticated, navigate, fetchProjects]);

  // Auto-select first project if none selected
  useEffect(() => {
    if (projects.length > 0 && !selectedProjectId) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId, setSelectedProjectId]);

  // Load posts and flights when project changes
  useEffect(() => {
    if (selectedProjectId) {
      setIsLoadingData(true);
      Promise.all([
        fetchPosts(selectedProjectId, { limit: 100 }),
        fetchFlights(selectedProjectId),
      ]).finally(() => setIsLoadingData(false));
    }
  }, [selectedProjectId, fetchPosts, fetchFlights]);

  // Calculate stats
  const stats = useMemo((): DashboardStats => {
    const now = new Date();
    const scheduledPosts = posts.filter(p => p.status === 'scheduled');
    const publishedPosts = posts.filter(p => p.status === 'published');
    const failedPosts = posts.filter(p => p.status === 'failed');
    const draftPosts = posts.filter(p => p.status === 'draft');
    const activeFlights = flights.filter(f => f.status === 'active');

    // Upcoming posts (scheduled, sorted by date, next 7 days)
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const upcoming = scheduledPosts
      .filter(p => {
        const postDate = new Date(p.scheduled_for);
        return postDate >= now && postDate <= sevenDaysFromNow;
      })
      .sort((a, b) => new Date(a.scheduled_for).getTime() - new Date(b.scheduled_for).getTime())
      .slice(0, 5);

    // Recently published (last 5)
    const recent = publishedPosts
      .sort((a, b) => new Date(b.scheduled_for).getTime() - new Date(a.scheduled_for).getTime())
      .slice(0, 5);

    // Recently edited - combine posts, flights, and projects
    const recentlyEditedItems: DashboardStats['recentlyEdited'] = [];

    // Add posts with updated_at
    posts.forEach(post => {
      if (post.updated_at) {
        recentlyEditedItems.push({
          type: 'post',
          id: post.id,
          displayId: post.display_id || '',
          title: post.text?.substring(0, 50) || t('dashboard:imageOnly'),
          updatedAt: post.updated_at,
          thumbnail: post.media_urls?.[0],
        });
      }
    });

    // Add flights with updated_at
    flights.forEach(flight => {
      if (flight.updated_at) {
        recentlyEditedItems.push({
          type: 'flight',
          id: flight.id,
          displayId: flight.display_id,
          title: flight.name,
          updatedAt: flight.updated_at,
          color: flight.color,
        });
      }
    });

    // Add current project with updated_at
    const currentProject = projects.find(p => p.id === selectedProjectId);
    if (currentProject) {
      recentlyEditedItems.push({
        type: 'project',
        id: currentProject.id,
        displayId: currentProject.display_id,
        title: currentProject.name,
        updatedAt: currentProject.updated_at,
      });
    }

    // Sort by updated_at descending and take top 5
    const recentlyEdited = recentlyEditedItems
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 5);

    return {
      totalPosts: posts.length,
      scheduledPosts: scheduledPosts.length,
      publishedPosts: publishedPosts.length,
      failedPosts: failedPosts.length,
      draftPosts: draftPosts.length,
      activeFlights: activeFlights.length,
      upcomingPosts: upcoming,
      recentPublished: recent,
      recentlyEdited,
    };
  }, [posts, flights, projects, selectedProjectId]);

  // Get flight for post
  const getFlightForPost = (post: Post): Flight | undefined => {
    return flights.find(f => f.id === post.flight_id);
  };

  // Format date
  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('de-DE', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format relative time (future)
  const formatRelativeTime = (date: string) => {
    const now = new Date();
    const postDate = new Date(date);
    const diff = postDate.getTime() - now.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return t('dashboard:time.inDays', { count: days });
    if (hours > 0) return t('dashboard:time.inHours', { count: hours });
    const minutes = Math.floor(diff / (1000 * 60));
    if (minutes > 0) return t('dashboard:time.inMinutes', { count: minutes });
    return t('dashboard:time.now');
  };

  // Format relative time (past - for "ago")
  const formatTimeAgo = (date: string) => {
    const now = new Date();
    const pastDate = new Date(date);
    const diff = now.getTime() - pastDate.getTime();
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return t('dashboard:time.daysAgo', { count: days });
    if (hours > 0) return t('dashboard:time.hoursAgo', { count: hours });
    if (minutes > 0) return t('dashboard:time.minutesAgo', { count: minutes });
    return t('dashboard:time.justNow');
  };

  if (!user) {
    return null;
  }

  const selectedProject = projects.find(p => p.id === selectedProjectId);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header with Project Selector */}
      <PageHeader
        icon={<HomeIcon className="w-7 h-7" />}
        title={t('dashboard:title')}
        subtitle={t('dashboard:welcome', { name: user.name?.split(' ')[0] || user.email })}
        actions={
          projects.length > 0 && (
            <select
              value={selectedProjectId || ''}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
            >
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          )
        }
      />

      {isLoadingData ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">{t('common:loading')}</div>
        </div>
      ) : !selectedProject ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
          <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('dashboard:noProjectExists')}</h3>
          <p className="text-gray-500 mb-4">{t('dashboard:createFirstProject')}</p>
          <Link
            to="/projects"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('dashboard:createProject')}
          </Link>
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          <div className="mb-8">
            <StatsCardGrid columns={6}>
              <StatsCard value={stats.totalPosts} label={t('dashboard:totalPosts')} config={GENERIC_STATS.posts} />
              <StatsCard value={stats.scheduledPosts} label={t('posts:status.scheduled')} config={POST_STATUS.scheduled} />
              <StatsCard value={stats.publishedPosts} label={t('posts:status.published')} config={POST_STATUS.published} />
              <StatsCard value={stats.draftPosts} label={t('posts:status.draft')} config={POST_STATUS.draft} />
              {stats.failedPosts > 0 && (
                <StatsCard value={stats.failedPosts} label={t('posts:status.failed')} config={POST_STATUS.failed} />
              )}
              <StatsCard value={stats.activeFlights} label={t('dashboard:activeFlights')} config={GENERIC_STATS.flights} />
            </StatsCardGrid>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upcoming Posts */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">{t('dashboard:upcomingPosts')}</h2>
                <Link
                  to={`/projects/${selectedProjectId}`}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {t('dashboard:viewAll')}
                </Link>
              </div>

              {stats.upcomingPosts.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-gray-500 text-sm">{t('dashboard:noUpcomingPosts')}</p>
                  <Link
                    to={`/projects/${selectedProjectId}`}
                    className="inline-block mt-3 text-sm text-blue-600 hover:text-blue-800"
                  >
                    {t('dashboard:planPost')}
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {stats.upcomingPosts.map((post) => {
                    const flight = getFlightForPost(post);
                    const statusConfig = POST_STATUS[post.status as keyof typeof POST_STATUS] || POST_STATUS.scheduled;
                    return (
                      <div
                        key={post.id}
                        onClick={() => navigate(`/posts?post=${post.id}`)}
                        className={`flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors border-l-[5px] ${statusConfig.accent} ${statusConfig.cardBg}`}
                      >
                        {/* Thumbnail - clickable to navigate to posts page */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/posts?post=${post.id}`);
                          }}
                          className="flex-shrink-0 hover:ring-2 hover:ring-blue-500 hover:ring-offset-1 rounded-lg transition-all"
                          title={t('dashboard:openInPosts')}
                        >
                          {post.media_urls && post.media_urls.length > 0 ? (
                            <img
                              src={getMediaUrl(post.media_urls[0])}
                              alt=""
                              className="w-12 h-12 object-cover rounded-lg"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                              <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </button>

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 line-clamp-1">
                            {post.text || <span className="text-gray-400 italic">{t('dashboard:imageOnly')}</span>}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-blue-600 font-medium">
                              {formatRelativeTime(post.scheduled_for)}
                            </span>
                            <span className="text-xs text-gray-400">
                              {formatDate(post.scheduled_for)}
                            </span>
                          </div>
                          {flight && (
                            <div className="flex items-center gap-1 mt-1">
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: flight.color || '#3B82F6' }}
                              />
                              <span className="text-xs text-gray-500">{flight.name}</span>
                            </div>
                          )}
                        </div>

                        {/* Platform badges */}
                        <div className="flex gap-1 flex-shrink-0">
                          {post.platforms?.map((platform) => (
                            <span
                              key={platform}
                              className={`px-1.5 py-0.5 text-xs rounded ${
                                platform === 'facebook'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-pink-100 text-pink-700'
                              }`}
                            >
                              {platform === 'facebook' ? 'FB' : 'IG'}
                            </span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Active Flights */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">{t('dashboard:activeFlights')}</h2>
                <Link
                  to="/flights"
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  {t('dashboard:viewAll')}
                </Link>
              </div>

              {flights.filter(f => f.status === 'active').length === 0 ? (
                <div className="text-center py-8">
                  <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <p className="text-gray-500 text-sm">{t('dashboard:noActiveFlights')}</p>
                  <Link
                    to={`/projects/${selectedProjectId}`}
                    className="inline-block mt-3 text-sm text-blue-600 hover:text-blue-800"
                  >
                    {t('dashboard:createFlight')}
                  </Link>
                </div>
              ) : (
                <div className="space-y-3">
                  {flights
                    .filter(f => f.status === 'active')
                    .slice(0, 5)
                    .map((flight) => {
                      const flightPosts = posts.filter(p => p.flight_id === flight.id);
                      const scheduledCount = flightPosts.filter(p => p.status === 'scheduled').length;
                      const publishedCount = flightPosts.filter(p => p.status === 'published').length;

                      // Calculate progress
                      const start = new Date(flight.start_date).getTime();
                      const end = new Date(flight.end_date).getTime();
                      const now = Date.now();
                      const progress = Math.min(100, Math.max(0, ((now - start) / (end - start)) * 100));

                      return (
                        <div
                          key={flight.id}
                          onClick={() => navigate(`/flights/${flight.id}`)}
                          className="p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors border border-gray-100"
                        >
                          <div className="flex items-center gap-3 mb-2">
                            <div
                              className="w-3 h-3 rounded-full flex-shrink-0"
                              style={{ backgroundColor: flight.color || '#3B82F6' }}
                            />
                            <span className="font-medium text-gray-900 truncate flex-1">
                              {flight.name}
                            </span>
                            <span className="text-xs text-gray-400">{flight.display_id}</span>
                          </div>

                          {/* Progress bar */}
                          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${progress}%`,
                                backgroundColor: flight.color || '#3B82F6',
                              }}
                            />
                          </div>

                          {/* Stats */}
                          <div className="flex items-center gap-4 text-xs">
                            <span className="text-blue-600">{scheduledCount} {t('dashboard:scheduled')}</span>
                            <span className="text-green-600">{publishedCount} {t('dashboard:published')}</span>
                            <span className="text-gray-400 ml-auto">
                              {Math.round(progress)}% {t('dashboard:completed')}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>

            {/* Recent Activity / Published */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">{t('dashboard:recentlyPublished')}</h2>
              </div>

              {stats.recentPublished.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-gray-500 text-sm">{t('dashboard:noPublishedPosts')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {stats.recentPublished.map((post) => (
                    <div
                      key={post.id}
                      onClick={() => navigate(`/posts?post=${post.id}`)}
                      className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors border-l-[5px] ${POST_STATUS.published.accent} ${POST_STATUS.published.cardBg} hover:brightness-95`}
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/posts?post=${post.id}`);
                        }}
                        className="flex-shrink-0 hover:ring-2 hover:ring-blue-500 hover:ring-offset-1 rounded transition-all"
                        title={t('dashboard:openInPosts')}
                      >
                        {post.media_urls && post.media_urls.length > 0 ? (
                          <img
                            src={getMediaUrl(post.media_urls[0])}
                            alt=""
                            className="w-10 h-10 object-cover rounded"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-green-100 rounded flex items-center justify-center">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 line-clamp-1">
                          {post.text || <span className="text-gray-400 italic">{t('dashboard:imageOnly')}</span>}
                        </p>
                        <p className="text-xs text-gray-500">{formatDate(post.scheduled_for)}</p>
                      </div>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full ${POST_STATUS.published.bg} ${POST_STATUS.published.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${POST_STATUS.published.dot}`} />
                        {t('posts:status.published')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Recently Edited */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">{t('dashboard:recentlyEdited')}</h2>
              </div>

              {stats.recentlyEdited.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <p className="text-gray-500 text-sm">{t('dashboard:noEdits')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {stats.recentlyEdited.map((item) => (
                    <div
                      key={`${item.type}-${item.id}`}
                      onClick={() => {
                        if (item.type === 'post') {
                          navigate(`/projects/${selectedProjectId}`);
                        } else if (item.type === 'flight') {
                          navigate(`/flights/${item.id}`);
                        } else {
                          navigate(`/projects/${item.id}`);
                        }
                      }}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      {/* Icon/Thumbnail based on type */}
                      {item.type === 'post' && item.thumbnail ? (
                        <img
                          src={getMediaUrl(item.thumbnail)}
                          alt=""
                          className="w-10 h-10 object-cover rounded flex-shrink-0"
                        />
                      ) : item.type === 'flight' ? (
                        <div
                          className="w-10 h-10 rounded flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${item.color || '#3B82F6'}20` }}
                        >
                          <svg className="w-5 h-5" style={{ color: item.color || '#3B82F6' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                        </div>
                      ) : item.type === 'project' ? (
                        <div className="w-10 h-10 bg-blue-50 rounded flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                        </div>
                      ) : (
                        <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                      )}

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 line-clamp-1">
                          {item.title}
                        </p>
                        <p className="text-xs text-gray-500">{formatTimeAgo(item.updatedAt)}</p>
                      </div>

                      {/* Type badge */}
                      <span className={`px-2 py-0.5 text-xs rounded-full ${
                        item.type === 'post'
                          ? 'bg-gray-100 text-gray-700'
                          : item.type === 'flight'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-blue-100 text-blue-700'
                      }`}>
                        {item.type === 'post' ? t('dashboard:post') : item.type === 'flight' ? t('dashboard:flight') : t('dashboard:project')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions & System Status */}
            <div className="space-y-6">
              {/* Quick Actions */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboard:quickActions')}</h2>
                <div className="grid grid-cols-2 gap-3">
                  <Link
                    to={`/projects/${selectedProjectId}`}
                    className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    <span className="text-sm font-medium">{t('dashboard:newPost')}</span>
                  </Link>
                  <Link
                    to="/creator"
                    className="flex items-center gap-2 p-3 rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm font-medium">{t('dashboard:openCreator')}</span>
                  </Link>
                  <Link
                    to="/media"
                    className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm font-medium">{t('dashboard:media')}</span>
                  </Link>
                  <Link
                    to="/flights"
                    className="flex items-center gap-2 p-3 rounded-lg bg-indigo-50 text-indigo-700 hover:bg-indigo-100 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                    <span className="text-sm font-medium">{t('dashboard:flights')}</span>
                  </Link>
                </div>
              </div>

              {/* System Status (compact) */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboard:systemStatus')}</h2>
                {health ? (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${health.status === 'ok' ? 'bg-green-500' : 'bg-yellow-500'}`} />
                      <span className="text-sm text-gray-600">API</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-full ${health.services.database === 'connected' ? 'bg-green-500' : 'bg-red-500'}`} />
                      <span className="text-sm text-gray-600">{t('dashboard:database')}</span>
                    </div>
                    <span className="text-xs text-gray-400 ml-auto">v{health.version}</span>
                  </div>
                ) : (
                  <div className="text-gray-500 text-sm">{t('common:loading')}</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
